import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DashboardHeader from "../components/DashboardHeader";
import Modal from "../components/PlannerPostModal";
import { getToken } from "../lib/session";

const API_BASE = process.env.REACT_APP_API || "http://localhost:6955/api";
const CONTENT_ENDPOINT = `${API_BASE}/dashboard/content`;
const BRANDS_ENDPOINT = `${API_BASE}/brands/user`;

export default function Dashboard() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const tab = params.get("tab") || "drafts";
  const [previewPost, setPreviewPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [rows, setRows] = useState([]);

  useEffect(() => {
    async function load() {
      setLoading(true);

      try {
        const token = getToken();

        console.log("[Dashboard] API_BASE:", API_BASE);
        console.log("[Dashboard] tab:", tab);
        console.log("[Dashboard] token exists:", Boolean(token));
        console.log("[Dashboard] authToken:", localStorage.getItem("authToken"));
        console.log("[Dashboard] auth.session:", localStorage.getItem("auth.session"));
        console.log("[Dashboard] user:", localStorage.getItem("user"));

        let url = "";
        let res;
        let data;

        if (tab === "drafts" || tab === "published") {
          const status = tab === "drafts" ? "draft" : "published";

          url = `${CONTENT_ENDPOINT}?status=${encodeURIComponent(
            status
          )}&q=${encodeURIComponent(query)}`;

          console.log("[Dashboard] Fetch content:", url);

          res = await fetch(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });

          if (!res.ok) {
            const txt = await res.text();
            console.error("[Dashboard] Content API failed:", res.status, txt);
            setRows([]);
            return;
          }

          data = await res.json();
          console.log("[Dashboard] Content response:", data);

          setRows(Array.isArray(data?.content) ? data.content : []);
          return;
        }

        if (tab === "brands") {
          url = `${BRANDS_ENDPOINT}?q=${encodeURIComponent(query)}`;

          console.log("[Dashboard] Fetch brands:", url);

          res = await fetch(url, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });

          if (!res.ok) {
            const txt = await res.text();
            console.error("[Dashboard] Brands API failed:", res.status, txt);
            setRows([]);
            return;
          }

          data = await res.json();
          console.log("[Dashboard] Brands response:", data);

          const normalizedBrands = Array.isArray(data)
            ? data
            : Array.isArray(data?.brands)
            ? data.brands
            : Array.isArray(data?.data)
            ? data.data
            : [];

          setRows(normalizedBrands);
          return;
        }

        setRows([]);
      } catch (err) {
        console.error("[Dashboard] Fetch error:", err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [tab, query]);

  const handleBrandClick = (brandId) => {
    navigate(`/brands/${brandId}`);
  };

  const handleDraftClick = (draft) => {
    setPreviewPost(draft);
  };

  const handleDeleteBrand = async (brandId) => {
    const confirmed = window.confirm("Are you sure you want to delete this brand?");
    if (!confirmed) return;

    try {
      const token = getToken();

      const res = await fetch(`${API_BASE}/brands/${brandId}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!res.ok) {
        const txt = await res.text();
        console.error("[Dashboard] Failed to delete brand:", res.status, txt);
        return;
      }

      setRows((prevRows) => prevRows.filter((brand) => brand._id !== brandId));
    } catch (error) {
      console.error("[Dashboard] Error deleting brand:", error);
    }
  };

  const handleDeleteDraft = async (draftId) => {
    const confirmed = window.confirm("Are you sure you want to delete this draft?");
    if (!confirmed) return;

    try {
      const token = getToken();

      const res = await fetch(`${API_BASE}/dashboard/content/${draftId}`, {
        method: "DELETE",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

      if (!res.ok) {
        const txt = await res.text();
        console.error("[Dashboard] Failed to delete draft:", res.status, txt);
        return;
      }

      setRows((prevRows) => prevRows.filter((draft) => draft._id !== draftId));
    } catch (error) {
      console.error("[Dashboard] Error deleting draft:", error);
    }
  };

  return (
    <div className="bg-white min-h-screen text-slate-800">
      <DashboardHeader />

      <main className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="text-3xl md:text-4xl font-extrabold text-center mb-8">
          My Content
        </h1>

        <div className="flex items-center gap-10 text-lg">
          <TabLink active={tab === "drafts"} label="Drafts" href="/dashboard?tab=drafts" />
          <TabLink active={tab === "published"} label="Published" href="/dashboard?tab=published" />
          <TabLink active={tab === "brands"} label="Brands" href="/dashboard?tab=brands" />
        </div>

        <div className="mt-6">
          <div className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 h-12">
            <span className="opacity-60">🔍</span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by title, brand name or website"
              className="flex-1 outline-none"
            />
            <button onClick={() => setQuery("")} className="opacity-60">
              ↻
            </button>
          </div>
        </div>

        <section className="mt-10">
          {loading ? (
            <div className="text-center text-slate-500">Loading…</div>
          ) : rows.length === 0 ? (
            <EmptyState tab={tab} />
          ) : tab === "brands" ? (
            <BrandGrid
              items={rows}
              onBrandClick={handleBrandClick}
              onDeleteClick={handleDeleteBrand}
            />
          ) : (
            <VideoGrid
              items={rows}
              onDraftClick={handleDraftClick}
              onDeleteClick={handleDeleteDraft}
            />
          )}
        </section>
      </main>

      {previewPost && (
        <Modal
          open={!!previewPost}
          post={previewPost}
          onClose={() => setPreviewPost(null)}
        />
      )}
    </div>
  );
}

function TabLink({ active, label, href }) {
  return (
    <a href={href} className="relative pb-2">
      <span className="hover:text-slate-900">{label}</span>
      {active && (
        <span className="absolute -bottom-[6px] left-0 right-0 h-[2px] bg-slate-800 rounded-full" />
      )}
    </a>
  );
}

function EmptyState({ tab }) {
  const txt =
    tab === "brands"
      ? "No brands yet"
      : tab === "published"
      ? "No published content yet"
      : "No drafts yet";

  return (
    <div className="py-16 grid place-items-center text-slate-500">
      <div className="w-48 h-48 rounded-full bg-slate-100 grid place-items-center mb-4">
        📦
      </div>
      <div>{txt}. Use “Create New” to get started.</div>
    </div>
  );
}

function VideoGrid({ items, onDraftClick, onDeleteClick }) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((v) => (
        <article
          key={v._id}
          className="border border-slate-200 rounded-xl p-4 cursor-pointer flex flex-col bg-white shadow-md hover:shadow-lg transition-transform transform hover:scale-105"
        >
          <div className="relative mb-4 h-48 bg-slate-100 rounded-lg overflow-hidden">
            {v.thumbUrl ? (
              <img
                src={v.thumbUrl}
                alt={v.title || "Draft Image"}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-400">
                No Image
              </div>
            )}
          </div>

          <div className="flex-1">
            <div className="text-sm text-slate-500 mb-2">
              {v.status === "published" ? "Published" : "Draft"} ·{" "}
              {new Date(v.updatedAt || v.createdAt).toLocaleString()}
            </div>
            <h3 className="font-semibold text-lg text-slate-800">
              {v.title || "Untitled"}
            </h3>
            {v.brand?.name && (
              <div className="text-sm text-slate-500 mt-1">{v.brand.name}</div>
            )}
          </div>

          <div className="mt-4 flex justify-between">
            <button
              onClick={() => onDraftClick(v)}
              className="text-blue-500 hover:text-blue-700"
            >
              Preview
            </button>
            <button
              onClick={() => onDeleteClick(v._id)}
              className="text-red-500 hover:text-red-700"
            >
              Delete
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}

function BrandGrid({ items, onBrandClick, onDeleteClick }) {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
      {items.map((b) => (
        <article
          key={b._id}
          className="border border-slate-200 rounded-xl p-4 cursor-pointer"
        >
          <h3 className="font-semibold">{b.name || b.website || "Brand"}</h3>
          <div className="text-sm text-slate-500 mt-1">{b.website}</div>

          <div className="mt-4 flex justify-between">
            <button
              onClick={() => onBrandClick(b._id)}
              className="text-blue-500 hover:text-blue-700"
            >
              View
            </button>
            <button
              onClick={() => onDeleteClick(b._id)}
              className="text-red-500 hover:text-red-700"
            >
              Delete
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}